import React, { useEffect, useState, useRef } from "react";
import './horizonTable.css';
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import onsiteproLogo from '../../assets/logo2 2.png';
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid, noopCreateSuccess } from "../../utils/formUuid";
import { buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  ADMIN_THEME,
  adminEditLabelStyle,
  adminEditInputStyle,
  parseSiteRegisterDate,
  formatSiteRegisterDate,
  siteRegisterDatePickerProps,
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditSigBoxStyle,
} from "./adminEditModalTheme";

function AccidentReportPage() {
  const canEditDelete = canEditDeleteRecords();
  const [appUsers, setAppUsers] = useState([]);
  const [accidentReports, setAccidentReports] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 4;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editReport, setEditReport] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [newReport, setNewReport] = useState({
    formUuid: '',
    reportNumber: '',
    accidentPersonName: '',
    accidentPersonAddress: '',
    accidentPersonCityTown: '',
    accidentPersonPostcode: '',
    accidentPersonTelephoneNumber: '',
    accidentPersonOccupation: '',
    accidentReportPersonName: '',
    accidentReportPersonAddress: '',
    accidentReportPersonCityTown: '',
    accidentReportPersonPostcode: '',
    accidentReportPersonTelephoneNumber: '',
    accidentReportPersonOccupation: '',
    accidentDetailsDate: '',
    accidentDetailsTime: '',
    accidentWhereInjury: '',
    accidentHowHappened: '',
    accidentDetailsOfInjury: '',
    dateOfficial: '',
    signatureOfficial: '',
    howReported: '',
    dateEmployers: '',
    signatureEmployers: ''
  });

  const [editOfficialSignature, setEditOfficialSignature] = useState("");
  const [editEmployersSignature, setEditEmployersSignature] = useState("");
  
  const officialSigCanvasRef = useRef(null);
  const employersSigCanvasRef = useRef(null);
  const editOfficialSigCanvasRef = useRef(null);
  const editEmployersSigCanvasRef = useRef(null);
  const generatingPDFRef = useRef(false); // Prevent double PDF generation
  const [uploadingKey, setUploadingKey] = useState(null);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  // Signature pad handlers for Add Modal - Official
  const clearOfficialSignature = () => {
    officialSigCanvasRef.current.clear();
    setNewReport(prev => ({ ...prev, signatureOfficial: "" }));
  };
  const saveOfficialSignature = () => {
    if (!officialSigCanvasRef.current.isEmpty()) {
      setNewReport(prev => ({
        ...prev,
        signatureOfficial: officialSigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png")
      }));
    }
  };

  // Signature pad handlers for Add Modal - Employers
  const clearEmployersSignature = () => {
    employersSigCanvasRef.current.clear();
    setNewReport(prev => ({ ...prev, signatureEmployers: "" }));
  };
  const saveEmployersSignature = () => {
    if (!employersSigCanvasRef.current.isEmpty()) {
      setNewReport(prev => ({
        ...prev,
        signatureEmployers: employersSigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png")
      }));
    }
  };

  // Signature pad handlers for Edit Modal - Official
  const clearEditOfficialSignature = () => {
    if (editOfficialSigCanvasRef.current) {
      editOfficialSigCanvasRef.current.clear();
    }
    setEditOfficialSignature("");
    setEditReport(prev => ({ ...prev, signatureOfficial: "" }));
  };
  const saveEditOfficialSignature = () => {
    if (editOfficialSigCanvasRef.current && !editOfficialSigCanvasRef.current.isEmpty()) {
      const sig = editOfficialSigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
      setEditOfficialSignature(sig);
      setEditReport(prev => ({ ...prev, signatureOfficial: sig }));
    }
  };

  // Signature pad handlers for Edit Modal - Employers
  const clearEditEmployersSignature = () => {
    if (editEmployersSigCanvasRef.current) {
      editEmployersSigCanvasRef.current.clear();
    }
    setEditEmployersSignature("");
    setEditReport(prev => ({ ...prev, signatureEmployers: "" }));
  };
  const saveEditEmployersSignature = () => {
    if (editEmployersSigCanvasRef.current && !editEmployersSigCanvasRef.current.isEmpty()) {
      const sig = editEmployersSigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
      setEditEmployersSignature(sig);
      setEditReport(prev => ({ ...prev, signatureEmployers: sig }));
    }
  };

  useEffect(() => {
    async function fetchAccidentReports() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/accident-report", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAccidentReports(data.data);
        } else {
          setAccidentReports([]);
        }
      } catch {
        setAccidentReports([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const data = await res.json();
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch {
        setAppUsers([]);
      }
    }
    fetchAccidentReports();
    fetchAppUsers();
  }, []);

  // Initialize signature canvas when edit modal opens
  useEffect(() => {
    if (showEditModal && editOfficialSigCanvasRef.current && editEmployersSigCanvasRef.current) {
      setTimeout(() => {
        if (editOfficialSigCanvasRef.current) {
          editOfficialSigCanvasRef.current.clear();
        }
        if (editEmployersSigCanvasRef.current) {
          editEmployersSigCanvasRef.current.clear();
        }
      }, 100);
    }
  }, [showEditModal]);

  // Improved search: split search string into words, ignore extra spaces, match if any word is present in any field
  const filtered = accidentReports.filter(r => {
    const fields = [r.reportNumber, r.accidentPersonName, r.accidentReportPersonName, r.accidentWhereInjury].map(val => (val || '').toLowerCase());
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (searchWords.length === 0) return true;
    return searchWords.every(word => fields.some(field => field.includes(word)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);
  const formatDateForDisplay = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  // Reset page to 1 if current page is beyond available pages
  if (filtered.length === 0 && page > 1) {
    setPage(1);
  }

  // Add Accident Report
  const handleAdd = async () => {
    // Basic validation
    if (!newReport.reportNumber || !newReport.accidentPersonName || !newReport.accidentDetailsDate || !newReport.accidentDetailsTime) {
      toast.error("Report Number, Person Name, Date and Time are required");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get user details for saving firstName and lastName if userId is available
      let addedByFirstName = '';
      let addedByLastName = '';
      if (newReport.userId) {
        const user = appUsers.find(u => u._id === newReport.userId);
        if (user) {
          addedByFirstName = user.firstName || '';
          addedByLastName = user.lastName || '';
        }
      }
      
      const reportToSave = {
        ...newReport,
        formUuid: ensureFormUuid(newReport.formUuid),
        addedByFirstName,
        addedByLastName
      };
      
      const res = await fetch(baseUrl + "/api/accident-report", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(reportToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (noopCreateSuccess(data)) {
          toast.warn(data.message || "This record already exists. No new record was added.");
          return;
        }
        setAccidentReports([...accidentReports, data.data]);
        setShowAddModal(false);
        setNewReport({
          formUuid: '',
          reportNumber: '',
          accidentPersonName: '',
          accidentPersonAddress: '',
          accidentPersonCityTown: '',
          accidentPersonPostcode: '',
          accidentPersonTelephoneNumber: '',
          accidentPersonOccupation: '',
          accidentReportPersonName: '',
          accidentReportPersonAddress: '',
          accidentReportPersonCityTown: '',
          accidentReportPersonPostcode: '',
          accidentReportPersonTelephoneNumber: '',
          accidentReportPersonOccupation: '',
          accidentDetailsDate: '',
          accidentDetailsTime: '',
          accidentWhereInjury: '',
          accidentHowHappened: '',
          accidentDetailsOfInjury: '',
          dateOfficial: '',
          signatureOfficial: '',
          howReported: '',
          dateEmployers: '',
          signatureEmployers: ''
        });
        toast.success("Accident report added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Edit Accident Report
  const handleEdit = (report) => {
    setEditReport({ ...report, formUuid: ensureFormUuid(report?.formUuid) });
    setEditOfficialSignature(report.signatureOfficial || "");
    setEditEmployersSignature(report.signatureEmployers || "");
    setShowEditModal(true);
    setTimeout(() => {
      if (editOfficialSigCanvasRef.current) {
        editOfficialSigCanvasRef.current.clear();
        if (report.signatureOfficial) {
          let sigSrc = report.signatureOfficial;
          if (!sigSrc.startsWith('data:image')) {
            sigSrc = 'data:image/png;base64,' + sigSrc;
          }
          const img = new window.Image();
          img.onload = () => {
            if (editOfficialSigCanvasRef.current) {
              const canvas = editOfficialSigCanvasRef.current.getCanvas();
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              const canvasAspect = canvas.width / canvas.height;
              const imgAspect = img.width / img.height;
              
              let drawWidth, drawHeight, drawX, drawY;
              
              if (imgAspect > canvasAspect) {
                drawWidth = canvas.width;
                drawHeight = canvas.width / imgAspect;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                drawHeight = canvas.height;
                drawWidth = canvas.height * imgAspect;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              }
              
              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
          };
          img.onerror = () => {
            console.error('Failed to load official signature image');
          };
          img.src = sigSrc;
        }
      }
      if (editEmployersSigCanvasRef.current) {
        editEmployersSigCanvasRef.current.clear();
        if (report.signatureEmployers) {
          let sigSrc = report.signatureEmployers;
          if (!sigSrc.startsWith('data:image')) {
            sigSrc = 'data:image/png;base64,' + sigSrc;
          }
          const img = new window.Image();
          img.onload = () => {
            if (editEmployersSigCanvasRef.current) {
              const canvas = editEmployersSigCanvasRef.current.getCanvas();
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              const canvasAspect = canvas.width / canvas.height;
              const imgAspect = img.width / img.height;
              
              let drawWidth, drawHeight, drawX, drawY;
              
              if (imgAspect > canvasAspect) {
                drawWidth = canvas.width;
                drawHeight = canvas.width / imgAspect;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                drawHeight = canvas.height;
                drawWidth = canvas.height * imgAspect;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              }
              
              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
          };
          img.onerror = () => {
            console.error('Failed to load employers signature image');
          };
          img.src = sigSrc;
        }
      }
    }, 300);
  };

  const handleEditSave = async () => {
    if (!editReport.reportNumber || !editReport.accidentPersonName || !editReport.accidentDetailsDate || !editReport.accidentDetailsTime) {
      toast.error("Report Number, Person Name, Date and Time are required");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get user details for saving firstName and lastName if userId is available
      let addedByFirstName = editReport.addedByFirstName || '';
      let addedByLastName = editReport.addedByLastName || '';
      if (editReport.userId) {
        const user = appUsers.find(u => u._id === editReport.userId);
        if (user) {
          addedByFirstName = user.firstName || '';
          addedByLastName = user.lastName || '';
        }
      }
      
      const reportToSave = {
        ...editReport,
        formUuid: ensureFormUuid(editReport.formUuid),
        addedByFirstName,
        addedByLastName
      };
      
      const res = await fetch(baseUrl + `/api/accident-report/${editReport._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(reportToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAccidentReports(accidentReports.map(r => r._id === editReport._id ? { ...editReport } : r));
        setShowEditModal(false);
        toast.success("Accident report updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  // Delete Accident Report
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/accident-report/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setAccidentReports(accidentReports.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.location || sharePointUploadRecord?.accidentWhereInjury || '').trim();

    if (!locationForUpload) {
      toast.error('Location is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  const generatePDF = async (record, options = {}) => {
    // Prevent double execution
    if (generatingPDFRef.current) {
      return;
    }
    generatingPDFRef.current = true;
    
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? {
      phaseType: options.phaseType || 'non-phase',
      phaseNumber: options.phaseNumber || '',
      network: options.network || '',
      siteName: options.siteName || '',
      onSuccess: options.onSuccess,
      onError: options.onError
    } : null;
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Modern color scheme
      const primaryColor = [139, 22, 52]; // #8b1634
      
      // Helper function to draw modern rounded rectangle
      const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor, hasShadow = false) => {
        if (hasShadow) {
          doc.setFillColor(0, 0, 0, 0.1);
          doc.roundedRect(x + 1, y + 1, width, height, radius, radius, 'F');
        }
        doc.setFillColor(...fillColor);
        doc.setDrawColor(...strokeColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(x, y, width, height, radius, radius, 'FD');
      };
      
      // ---- HEADER SECTION ----
      const headerHeight = 30;
      drawRoundedRect(0, 0, pageWidth, headerHeight, 0, primaryColor, primaryColor);
      
      // ---- ADD HIGH-RES LOGO ----
      const addLogoToPDF = () => {
        return new Promise((resolve) => {
          const logoImg = new Image();
          logoImg.crossOrigin = 'anonymous';
          logoImg.onload = function () {
            try {
              const logoWidth = 70;
              const aspectRatio = logoImg.height / logoImg.width;
              const logoHeight = logoWidth * aspectRatio;
              const logoX = 10;
              const logoY = (headerHeight - logoHeight) / 2;
              
              const canvas = document.createElement('canvas');
              const scale = 2.5;
              canvas.width = logoImg.width * scale;
              canvas.height = logoImg.height * scale;
              const ctx = canvas.getContext('2d');
              ctx.scale(scale, scale);
              ctx.drawImage(logoImg, 0, 0);
              
              const highResDataUrl = canvas.toDataURL('image/png', 1.0);
              doc.addImage(highResDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight, '', 'NONE');
            } catch (error) {
              doc.setFillColor(255, 255, 255);
              doc.rect(10, 6, 60, 15, 'F');
              doc.setFont("helvetica", "bold");
              doc.setFontSize(10);
              doc.setTextColor(0, 0, 0);
              doc.text("TAYLOR TECHNOLOGY", 15, 12);
              doc.text("SYSTEMS LIMITED", 15, 17);
            }
            resolve();
          };
          logoImg.onerror = function () {
            doc.setFillColor(255, 255, 255);
            doc.rect(10, 6, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("TAYLOR TECHNOLOGY", 15, 12);
            doc.text("SYSTEMS LIMITED", 15, 17);
            resolve();
          };
          logoImg.src = onsiteproLogo;
        });
      };
      
      await addLogoToPDF();
      
      // ---- HEADER TEXT ----
      const titleX = 90;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text("Accident Report", titleX, 13);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", titleX, 19);
      
      let yPosition = headerHeight + 10;
      
      // ---- Report Number (Right-aligned Badge) ----
      if (record.reportNumber && String(record.reportNumber).trim()) {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const reportText = `Report No: ${String(record.reportNumber)}`;
        const textWidth = doc.getTextWidth(reportText);
        
        // Badge dimensions - compact and professional
        const reportBoxWidth = textWidth + 12;
        const reportBoxHeight = 10;
        // Right-aligned position
        const reportBoxX = pageWidth - reportBoxWidth - 10;
        const reportBoxY = yPosition;
        
        // White background with thin red border
        doc.setFillColor(255, 255, 255);
        doc.setDrawColor(139, 22, 52);
        doc.setLineWidth(0.5);
        doc.roundedRect(reportBoxX, reportBoxY, reportBoxWidth, reportBoxHeight, 1, 1, "FD");
        
        // Centered text in bold red
        doc.setTextColor(139, 22, 52);
        const textX = reportBoxX + (reportBoxWidth - textWidth) / 2;
        doc.text(reportText, textX, reportBoxY + 7);
        
        yPosition += reportBoxHeight + 10;
      }
      
      // ---- Section 2: About the Person Who Had The Accident ----
      const personFields = [
        { label: "Name:", field: "accidentPersonName" },
        { label: "Address:", field: "accidentPersonAddress" },
        { label: "City/Town:", field: "accidentPersonCityTown" },
        { label: "Postcode:", field: "accidentPersonPostcode" },
        { label: "Telephone:", field: "accidentPersonTelephoneNumber" },
        { label: "Occupation:", field: "accidentPersonOccupation" },
      ].filter(f => record[f.field] && String(record[f.field]).trim());
      
      if (personFields.length > 0) {
        if (yPosition + 50 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.rect(10, yPosition, pageWidth - 20, 8, "F");
        doc.text("1", 14, yPosition + 5.5);
        doc.text("ABOUT THE PERSON WHO HAD THE ACCIDENT", 22, yPosition + 5.5);
        yPosition += 8;
        
        const personTableBody = personFields.map(field => [
          { content: field.label, styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: String(record[field.field] || ""), styles: { halign: "left" } },
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 140 },
          },
          head: [],
          body: personTableBody,
          styles: {
            font: "helvetica",
            fontSize: 9,
            cellPadding: 3,
            overflow: "linebreak",
            valign: "middle",
          },
          theme: "grid",
          tableWidth: pageWidth - 20,
          margin: { left: 10 },
          headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            lineWidth: 0.2,
          },
          bodyStyles: {
            lineWidth: 0.2,
            textColor: [0, 0, 0],
          },
        });
        
        yPosition = doc.lastAutoTable.finalY + 2;
      }
      
      // ---- Section 3: Details of Person Reporting This Accident ----
      const reportPersonFields = [
        { label: "Name:", field: "accidentReportPersonName" },
        { label: "Address:", field: "accidentReportPersonAddress" },
        { label: "City/Town:", field: "accidentReportPersonCityTown" },
        { label: "Postcode:", field: "accidentReportPersonPostcode" },
        { label: "Telephone:", field: "accidentReportPersonTelephoneNumber" },
        { label: "Occupation:", field: "accidentReportPersonOccupation" },
      ].filter(f => record[f.field] && String(record[f.field]).trim());
      
      if (reportPersonFields.length > 0) {
        if (yPosition + 50 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.rect(10, yPosition, pageWidth - 20, 8, "F");
        doc.text("2", 14, yPosition + 5.5);
        doc.text("DETAILS OF PERSON REPORTING THIS ACCIDENT", 22, yPosition + 5.5);
        yPosition += 8;
        
        const reportPersonTableBody = reportPersonFields.map(field => [
          { content: field.label, styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
          { content: String(record[field.field] || ""), styles: { halign: "left" } },
        ]);
        
        autoTable(doc, {
          startY: yPosition,
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 140 },
          },
          head: [],
          body: reportPersonTableBody,
          styles: {
            font: "helvetica",
            fontSize: 9,
            cellPadding: 3,
            overflow: "linebreak",
            valign: "middle",
          },
          theme: "grid",
          tableWidth: pageWidth - 20,
          margin: { left: 10 },
          headStyles: {
            fillColor: [245, 245, 245],
            textColor: [0, 0, 0],
            lineWidth: 0.2,
          },
          bodyStyles: {
            lineWidth: 0.2,
            textColor: [0, 0, 0],
          },
        });
        
        yPosition = doc.lastAutoTable.finalY + 2;
      }
      
      // ---- Section 4: Details of Accident/Injury ----
      const accidentFields = [
        { label: "Date:", field: "accidentDetailsDate" },
        { label: "Time:", field: "accidentDetailsTime" },
        { label: "Where:", field: "accidentWhereInjury" },
        { label: "How happened:", field: "accidentHowHappened" },
        { label: "Details of injury:", field: "accidentDetailsOfInjury" },
      ].filter(f => record[f.field] && String(record[f.field]).trim());
      
      if (accidentFields.length > 0 || record.signatureOfficial || record.dateOfficial) {
        if (yPosition + 80 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.rect(10, yPosition, pageWidth - 20, 8, "F");
        doc.text("3", 14, yPosition + 5.5);
        doc.text("DETAILS OF ACCIDENT/INJURY", 22, yPosition + 5.5);
        yPosition += 8;
        
        if (accidentFields.length > 0) {
          const accidentTableBody = accidentFields.map(field => [
            { content: field.label, styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: String(record[field.field] || ""), styles: { halign: "left" } },
          ]);
          
          autoTable(doc, {
            startY: yPosition,
            columnStyles: {
              0: { cellWidth: 50 },
              1: { cellWidth: 140 },
            },
            head: [],
            body: accidentTableBody,
            styles: {
              font: "helvetica",
              fontSize: 9,
              cellPadding: 3,
              overflow: "linebreak",
              valign: "middle",
            },
            theme: "grid",
            tableWidth: pageWidth - 20,
            margin: { left: 10 },
            headStyles: {
              fillColor: [245, 245, 245],
              textColor: [0, 0, 0],
              lineWidth: 0.2,
            },
            bodyStyles: {
              lineWidth: 0.2,
              textColor: [0, 0, 0],
            },
          });
          
          yPosition = doc.lastAutoTable.finalY + 2;
        }
      }
      
      // Add Official Signature (within Section 4)
      if (record.signatureOfficial || record.dateOfficial) {
        if (yPosition + 50 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        yPosition += 5;
        
        const boxWidth = pageWidth - 20;
        const boxHeight = 40;
        
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(248, 248, 248);
        doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
        
        doc.setTextColor(139, 22, 52);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        if (record.dateOfficial) {
          doc.text(`Official Signature - Date: ${record.dateOfficial}`, 15, yPosition + 8);
        } else {
          doc.text("Official Signature", 15, yPosition + 8);
        }
        
        if (record.signatureOfficial) {
          // Helper function to add signature with proper sizing
          const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
            if (!signatureData) return;
            
            try {
              let sigSrc = signatureData;
              if (!sigSrc.startsWith('data:image')) {
                sigSrc = 'data:image/png;base64,' + sigSrc;
              }
              
              return new Promise((resolve) => {
                const sigImg = new Image();
                sigImg.crossOrigin = 'anonymous';
                
                sigImg.onload = () => {
                  try {
                    const aspectRatio = sigImg.width / sigImg.height;
                    let imgWidth = maxWidth;
                    let imgHeight = maxWidth / aspectRatio;
                    
                    if (imgHeight > maxHeight) {
                      imgHeight = maxHeight;
                      imgWidth = maxHeight * aspectRatio;
                    }
                    
                    imgWidth = Math.min(imgWidth, maxWidth);
                    imgHeight = Math.min(imgHeight, maxHeight);
                    
                    const scale = 4;
                    const canvas = document.createElement('canvas');
                    canvas.width = Math.round(imgWidth * scale);
                    canvas.height = Math.round(imgHeight * scale);
                    const ctx = canvas.getContext('2d');
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.fillStyle = '#FFFFFF';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);
                    const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                    doc.addImage(highResDataUrl, 'PNG', x, y, imgWidth, imgHeight, '', 'NONE');
                  } catch (err) {
                    console.error('Error adding signature to PDF:', err);
                    try {
                      doc.addImage(sigSrc, 'PNG', x, y, maxWidth, maxHeight);
                    } catch (fallbackErr) {
                      console.error('Fallback signature addition also failed:', fallbackErr);
                    }
                  }
                  resolve();
                };
                
                sigImg.onerror = () => {
                  console.error('Error loading signature image');
                  resolve();
                };
                
                sigImg.src = sigSrc;
              });
            } catch (err) {
              console.error('Signature processing error:', err);
            }
          };
          
          const signatureMaxWidth = 50;
          const signatureMaxHeight = 22;
          const signatureX = pageWidth - 75;
          const signatureY = yPosition + 12;
          
          doc.setFontSize(8);
          doc.setTextColor(139, 22, 52);
          doc.setFont("helvetica", "bold");
          doc.text("Signature:", signatureX, signatureY - 2);
          
          await addSignatureToPDF(record.signatureOfficial, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
        }
        
        yPosition += boxHeight + 8;
      }
      
      // ---- Section 5: Employers Use Only ----
      const employersFields = [
        { label: "How was it reported?:", field: "howReported" },
        { label: "Date:", field: "dateEmployers" },
      ].filter(f => record[f.field] && String(record[f.field]).trim());
      
      if (employersFields.length > 0 || record.signatureEmployers) {
        if (yPosition + 50 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFillColor(139, 22, 52);
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.rect(10, yPosition, pageWidth - 20, 8, "F");
        doc.text("4", 14, yPosition + 5.5);
        doc.text("EMPLOYERS USE ONLY", 22, yPosition + 5.5);
        yPosition += 8;
        
        if (employersFields.length > 0 || record.signatureEmployers) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0);
          doc.text("If this incident is reportable under RIDDOR (Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 1995)", 15, yPosition + 8);
          yPosition += 12;
        }
        
        if (employersFields.length > 0) {
          const employersTableBody = employersFields.map(field => [
            { content: field.label, styles: { fillColor: [230, 230, 230], fontStyle: "bold" } },
            { content: String(record[field.field] || ""), styles: { halign: "left" } },
          ]);
          
          autoTable(doc, {
            startY: yPosition,
            columnStyles: {
              0: { cellWidth: 50 },
              1: { cellWidth: 140 },
            },
            head: [],
            body: employersTableBody,
            styles: {
              font: "helvetica",
              fontSize: 9,
              cellPadding: 3,
              overflow: "linebreak",
              valign: "middle",
            },
            theme: "grid",
            tableWidth: pageWidth - 20,
            margin: { left: 10 },
            headStyles: {
              fillColor: [245, 245, 245],
              textColor: [0, 0, 0],
              lineWidth: 0.2,
            },
            bodyStyles: {
              lineWidth: 0.2,
              textColor: [0, 0, 0],
            },
          });
          
          yPosition = doc.lastAutoTable.finalY + 2;
        }
      }
      
      // Add Employers Signature
      if (record.signatureEmployers) {
        if (yPosition + 50 > pageHeight - 30) {
          doc.addPage();
          yPosition = 20;
        }
        
        yPosition += 5;
        
        const boxWidth = pageWidth - 20;
        const boxHeight = 40;
        
        doc.setDrawColor(200, 200, 200);
        doc.setFillColor(248, 248, 248);
        doc.roundedRect(10, yPosition, boxWidth, boxHeight, 3, 3, "FD");
        
        doc.setTextColor(139, 22, 52);
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        if (record.dateEmployers) {
          doc.text(`Employers Signature - Date: ${record.dateEmployers}`, 15, yPosition + 8);
        } else {
          doc.text("Employers Signature", 15, yPosition + 8);
        }
        
        // Helper function to add signature with proper sizing
        const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight) => {
          if (!signatureData) return;
          
          try {
            let sigSrc = signatureData;
            if (!sigSrc.startsWith('data:image')) {
              sigSrc = 'data:image/png;base64,' + sigSrc;
            }
            
            return new Promise((resolve) => {
              const sigImg = new Image();
              sigImg.crossOrigin = 'anonymous';
              
              sigImg.onload = () => {
                try {
                  const aspectRatio = sigImg.width / sigImg.height;
                  let imgWidth = maxWidth;
                  let imgHeight = maxWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = maxHeight * aspectRatio;
                  }
                  
                  imgWidth = Math.min(imgWidth, maxWidth);
                  imgHeight = Math.min(imgHeight, maxHeight);
                  
                  const scale = 4;
                  const canvas = document.createElement('canvas');
                  canvas.width = Math.round(imgWidth * scale);
                  canvas.height = Math.round(imgHeight * scale);
                  const ctx = canvas.getContext('2d');
                  ctx.imageSmoothingEnabled = true;
                  ctx.imageSmoothingQuality = 'high';
                  ctx.fillStyle = '#FFFFFF';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  ctx.drawImage(sigImg, 0, 0, sigImg.width, sigImg.height, 0, 0, canvas.width, canvas.height);
                  const highResDataUrl = canvas.toDataURL('image/png', 1.0);
                  doc.addImage(highResDataUrl, 'PNG', x, y, imgWidth, imgHeight, '', 'NONE');
                } catch (err) {
                  console.error('Error adding signature to PDF:', err);
                  try {
                    doc.addImage(sigSrc, 'PNG', x, y, maxWidth, maxHeight);
                  } catch (fallbackErr) {
                    console.error('Fallback signature addition also failed:', fallbackErr);
                  }
                }
                resolve();
              };
              
              sigImg.onerror = () => {
                console.error('Error loading signature image');
                resolve();
              };
              
              sigImg.src = sigSrc;
            });
          } catch (err) {
            console.error('Signature processing error:', err);
          }
        };
        
        const signatureMaxWidth = 50;
        const signatureMaxHeight = 22;
        const signatureX = pageWidth - 75;
        const signatureY = yPosition + 12;
        
        doc.setFontSize(8);
        doc.setTextColor(139, 22, 52);
        doc.setFont("helvetica", "bold");
        doc.text("Signature:", signatureX, signatureY - 2);
        
        await addSignatureToPDF(record.signatureEmployers, signatureX, signatureY, signatureMaxWidth, signatureMaxHeight);
        yPosition += boxHeight + 8;
      }
      
      // ---- Footer Section ----
      const footerY = pageHeight - 25;
      drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`,
        20,
        footerY + 8
      );
      doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
      
      // Decorative border removed
      
      // Build filename and either save or upload
      const safeName = String(record.reportNumber || 'AccidentReport').replace(/[\\/:"*?<>|]+/g, '-');
      const safeDate = String(record.accidentDetailsDate || 'Report').replace(/[\\/:"*?<>|]+/g, '-');
      
      let fileName;
      if (shouldUploadToSharePoint && uploadConfig) {
        const locationForUpload = record.location || record.accidentWhereInjury || '';
        fileName = buildSharePointFileName([
          addedByName(record),
          locationForUpload,
          toDdMmYyyy(record.accidentDetailsDate),
        ]);
      } else {
        fileName = `AccidentReport_${safeName}_${safeDate}.pdf`;
      }
      
      if (shouldUploadToSharePoint && uploadConfig) {
        try {
          const locationForUpload = record.location || record.accidentWhereInjury || '';
          const key = `${record._id || ''}|${locationForUpload}|${record.accidentDetailsDate || ''}`;
          setUploadingKey(key);
          const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
          const dataUri = doc.output('datauristring');
          
          // Prepare SharePoint upload parameters
          const uploadParams = {
            fileName,
            fileBase64: dataUri,
            phaseType: uploadConfig.phaseType,
            phaseNumber: uploadConfig.phaseNumber,
            network: uploadConfig.network,
            siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "Accident Report"
          };
          
          const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
            method: 'POST',
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: JSON.stringify(uploadParams)
          });
          if (res.status !== 204 && !res.ok) {
            let msg = 'SharePoint upload failed';
            try {
              const out = await res.json();
              msg = out?.message || msg;
            } catch (_) {}
            throw new Error(msg);
          }
          toast.success("PDF uploaded to SharePoint successfully!");
          if (uploadConfig.onSuccess) {
            uploadConfig.onSuccess();
          }
        } catch (err) {
          toast.error(err?.message || "SharePoint upload failed");
          if (uploadConfig.onError) {
            uploadConfig.onError(err);
          }
        } finally {
          setUploadingKey(null);
        }
      } else {
        doc.save(fileName);
        toast.success("Professional Accident Report PDF generated successfully!");
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.error('Failed to generate PDF');
    } finally {
      generatingPDFRef.current = false;
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Accident Report</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1.2fr 1.2fr 0.9fr 0.9fr 1.2fr 1fr 0.9fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Report Number</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Person Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Report Person</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Time</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Location</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else {
                const user = appUsers.find(u => u._id === r.userId);
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1.2fr 1.2fr 0.9fr 0.9fr 1.2fr 1fr 0.9fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.reportNumber || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.accidentPersonName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.accidentReportPersonName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.accidentDetailsDate || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.accidentDetailsTime || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.accidentWhereInjury || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDateForDisplay(r.updatedAt || r.createdAt || r.accidentDetailsDate)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.accidentWhereInjury || ''}|${r.accidentDetailsDate || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleDelete(r._id)}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add Accident Report</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                {/* Report Number Field */}
                <div className="row mb-3">
                  <div className="col-6">
                    <label>Enter Report No</label>
                    <input type="text" className="form-control" placeholder="Enter Report No" value={newReport.reportNumber} onChange={e=>{
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setNewReport({...newReport, reportNumber: value});
                    }} />
                  </div>
                </div>

                {/* Section 1: Person Who Had The Accident */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>1. ABOUT THE PERSON WHO HAD THE ACCIDENT</h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Name</label>
                        <input type="text" className="form-control" value={newReport.accidentPersonName} onChange={e=>setNewReport({...newReport, accidentPersonName: e.target.value})} />
                      </div>
                      <div className="col-6">
                        <label>Address</label>
                        <input type="text" className="form-control" value={newReport.accidentPersonAddress} onChange={e=>setNewReport({...newReport, accidentPersonAddress: e.target.value})} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4">
                        <label>City/Town</label>
                        <input type="text" className="form-control" value={newReport.accidentPersonCityTown} onChange={e=>setNewReport({...newReport, accidentPersonCityTown: e.target.value})} />
                      </div>
                      <div className="col-4">
                        <label>Postcode</label>
                        <input type="text" className="form-control" value={newReport.accidentPersonPostcode} onChange={e=>setNewReport({...newReport, accidentPersonPostcode: e.target.value})} />
                      </div>
                      <div className="col-4">
                        <label>Telephone</label>
                        <input type="text" className="form-control" value={newReport.accidentPersonTelephoneNumber} onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setNewReport({...newReport, accidentPersonTelephoneNumber: value});
                        }} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Occupation</label>
                        <input type="text" className="form-control" value={newReport.accidentPersonOccupation} onChange={e=>setNewReport({...newReport, accidentPersonOccupation: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Details of Person Reporting This Accident */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>2. DETAILS OF PERSON REPORTING THIS ACCIDENT</h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Name</label>
                        <input type="text" className="form-control" value={newReport.accidentReportPersonName} onChange={e=>setNewReport({...newReport, accidentReportPersonName: e.target.value})} />
                      </div>
                      <div className="col-6">
                        <label>Address</label>
                        <input type="text" className="form-control" value={newReport.accidentReportPersonAddress} onChange={e=>setNewReport({...newReport, accidentReportPersonAddress: e.target.value})} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-4">
                        <label>City/Town</label>
                        <input type="text" className="form-control" value={newReport.accidentReportPersonCityTown} onChange={e=>setNewReport({...newReport, accidentReportPersonCityTown: e.target.value})} />
                      </div>
                      <div className="col-4">
                        <label>Postcode</label>
                        <input type="text" className="form-control" value={newReport.accidentReportPersonPostcode} onChange={e=>setNewReport({...newReport, accidentReportPersonPostcode: e.target.value})} />
                      </div>
                      <div className="col-4">
                        <label>Telephone</label>
                        <input type="text" className="form-control" value={newReport.accidentReportPersonTelephoneNumber} onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setNewReport({...newReport, accidentReportPersonTelephoneNumber: value});
                        }} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Occupation</label>
                        <input type="text" className="form-control" value={newReport.accidentReportPersonOccupation} onChange={e=>setNewReport({...newReport, accidentReportPersonOccupation: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Details of Accident/Injury */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>3. DETAILS OF ACCIDENT/INJURY</h6>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Date</label>
                        <input type="date" className="form-control" value={newReport.accidentDetailsDate ? newReport.accidentDetailsDate.split('/').reverse().join('-') : ''} onChange={e=>setNewReport({...newReport, accidentDetailsDate: e.target.value.split('-').reverse().join('/')})} />
                      </div>
                      <div className="col-6">
                        <label>Time</label>
                        <input type="time" className="form-control" value={newReport.accidentDetailsTime} onChange={e=>setNewReport({...newReport, accidentDetailsTime: e.target.value})} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-12">
                        <label>Where did the accident/injury take place?</label>
                        <input type="text" className="form-control" value={newReport.accidentWhereInjury} onChange={e=>setNewReport({...newReport, accidentWhereInjury: e.target.value})} />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-12">
                        <label>How did the accident happen?</label>
                        <textarea className="form-control" rows="3" value={newReport.accidentHowHappened} onChange={e=>setNewReport({...newReport, accidentHowHappened: e.target.value})}></textarea>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-12">
                        <label>Details of accident/injury</label>
                        <textarea className="form-control" rows="3" value={newReport.accidentDetailsOfInjury} onChange={e=>setNewReport({...newReport, accidentDetailsOfInjury: e.target.value})}></textarea>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>Date</label>
                        <input type="date" className="form-control" value={newReport.dateOfficial ? newReport.dateOfficial.split('/').reverse().join('-') : ''} onChange={e=>setNewReport({...newReport, dateOfficial: e.target.value.split('-').reverse().join('/')})} />
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label fw-semibold text-secondary">Official Signature</label>
                      <div style={{
                        width: '100%',
                        border: '2px solid #8b1634',
                        borderRadius: '18px',
                        background: '#e9cfd6',
                        padding: '0.5rem',
                        textAlign: 'left',
                        position: 'relative',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <SignatureCanvas
                          ref={officialSigCanvasRef}
                          penColor="#8b1634"
                          backgroundColor="#e9cfd6"
                          clearOnResize={false}
                          velocityFilterWeight={0.7}
                          minWidth={1}
                          maxWidth={2.5}
                          dotSize={1.5}
                          throttle={16}
                          canvasProps={{
                            width: 350,
                            height: 100,
                            style: {
                              width: '100%',
                              height: '100px',
                              border: 'none',
                              borderRadius: '18px',
                              background: '#e9cfd6',
                              display: 'block',
                              touchAction: 'none'
                            }
                          }}
                          onEnd={saveOfficialSignature}
                        />
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: '4px'
                        }}>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            style={{
                              background: 'transparent',
                              color: '#8b1634',
                              borderColor: '#8b1634'
                            }}
                            onClick={clearOfficialSignature}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Employers Use Only */}
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 style={{color: '#8b1634', fontWeight: 'bold', marginBottom: '15px'}}>4. EMPLOYERS USE ONLY</h6>
                    <div className="row mb-2">
                      <div className="col-12">
                        <p style={{color: '#000', marginBottom: '15px'}}>If this incident is reportable under RIDDOR (Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 1995)</p>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-6">
                        <label>How was it reported?</label>
                        <input type="text" className="form-control" value={newReport.howReported} onChange={e=>setNewReport({...newReport, howReported: e.target.value})} />
                      </div>
                      <div className="col-6">
                        <label>Date</label>
                        <input type="date" className="form-control" value={newReport.dateEmployers ? newReport.dateEmployers.split('/').reverse().join('-') : ''} onChange={e=>setNewReport({...newReport, dateEmployers: e.target.value.split('-').reverse().join('/')})} />
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label className="form-label fw-semibold text-secondary">Employers Signature</label>
                      <div style={{
                        width: '100%',
                        border: '2px solid #8b1634',
                        borderRadius: '18px',
                        background: '#e9cfd6',
                        padding: '0.5rem',
                        textAlign: 'left',
                        position: 'relative',
                        minHeight: '120px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        <SignatureCanvas
                          ref={employersSigCanvasRef}
                          penColor="#8b1634"
                          backgroundColor="#e9cfd6"
                          clearOnResize={false}
                          velocityFilterWeight={0.7}
                          minWidth={1}
                          maxWidth={2.5}
                          dotSize={1.5}
                          throttle={16}
                          canvasProps={{
                            width: 350,
                            height: 100,
                            style: {
                              width: '100%',
                              height: '100px',
                              border: 'none',
                              borderRadius: '18px',
                              background: '#e9cfd6',
                              display: 'block',
                              touchAction: 'none'
                            }
                          }}
                          onEnd={saveEmployersSignature}
                        />
                        <div style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          marginTop: '4px'
                        }}>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            style={{
                              background: 'transparent',
                              color: '#8b1634',
                              borderColor: '#8b1634'
                            }}
                            onClick={clearEmployersSignature}>
                            Clear
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-12">
                        <p style={{color: '#000', fontSize: '12px', marginTop: '15px'}}>Please Note: To comply with the Data Protection Act 1998 (DPA) personal details entered on accident record forms must be kept confidential.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editReport && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>Edit Accident Report</h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)}></button>
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                {/* Report Number Field */}
                <div className="row g-3 mb-3">
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Enter Report No</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} placeholder="Enter Report No" value={editReport.reportNumber} onChange={e=>{
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setEditReport({...editReport, reportNumber: value});
                    }} />
                  </div>
                </div>

                {/* Section 1: Person Who Had The Accident */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>1. ABOUT THE PERSON WHO HAD THE ACCIDENT</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Name</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentPersonName} onChange={e=>setEditReport({...editReport, accidentPersonName: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Address</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentPersonAddress} onChange={e=>setEditReport({...editReport, accidentPersonAddress: e.target.value})} />
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-4">
                        <label style={adminEditLabelStyle}>City/Town</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentPersonCityTown} onChange={e=>setEditReport({...editReport, accidentPersonCityTown: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-4">
                        <label style={adminEditLabelStyle}>Postcode</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentPersonPostcode} onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setEditReport({...editReport, accidentPersonPostcode: value});
                        }} pattern="[0-9]*" inputMode="numeric" />
                      </div>
                      <div className="col-12 col-md-4">
                        <label style={adminEditLabelStyle}>Telephone</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentPersonTelephoneNumber} onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setEditReport({...editReport, accidentPersonTelephoneNumber: value});
                        }} pattern="[0-9]*" inputMode="numeric" />
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Occupation</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentPersonOccupation} onChange={e=>setEditReport({...editReport, accidentPersonOccupation: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 3: Details of Person Reporting This Accident */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>2. DETAILS OF PERSON REPORTING THIS ACCIDENT</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Name</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentReportPersonName} onChange={e=>setEditReport({...editReport, accidentReportPersonName: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Address</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentReportPersonAddress} onChange={e=>setEditReport({...editReport, accidentReportPersonAddress: e.target.value})} />
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-4">
                        <label style={adminEditLabelStyle}>City/Town</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentReportPersonCityTown} onChange={e=>setEditReport({...editReport, accidentReportPersonCityTown: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-4">
                        <label style={adminEditLabelStyle}>Postcode</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentReportPersonPostcode} onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setEditReport({...editReport, accidentReportPersonPostcode: value});
                        }} pattern="[0-9]*" inputMode="numeric" />
                      </div>
                      <div className="col-12 col-md-4">
                        <label style={adminEditLabelStyle}>Telephone</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentReportPersonTelephoneNumber} onChange={e=>{
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          setEditReport({...editReport, accidentReportPersonTelephoneNumber: value});
                        }} pattern="[0-9]*" inputMode="numeric" />
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Occupation</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentReportPersonOccupation} onChange={e=>setEditReport({...editReport, accidentReportPersonOccupation: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Details of Accident/Injury */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>3. DETAILS OF ACCIDENT/INJURY</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Date</label>
                        <div className="site-register-datepicker-wrap">
                          <DatePicker
                            {...siteRegisterDatePickerProps}
                            selected={parseSiteRegisterDate(editReport.accidentDetailsDate)}
                            onChange={(d) => setEditReport({...editReport, accidentDetailsDate: formatSiteRegisterDate(d)})}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Time</label>
                        <input type="time" className="form-control" style={adminEditInputStyle} value={editReport.accidentDetailsTime} onChange={e=>setEditReport({...editReport, accidentDetailsTime: e.target.value})} />
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <label style={adminEditLabelStyle}>Where did the accident/injury take place?</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.accidentWhereInjury} onChange={e=>setEditReport({...editReport, accidentWhereInjury: e.target.value})} />
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <label style={adminEditLabelStyle}>How did the accident happen?</label>
                        <textarea className="form-control" style={adminEditInputStyle} rows="3" value={editReport.accidentHowHappened} onChange={e=>setEditReport({...editReport, accidentHowHappened: e.target.value})}></textarea>
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <label style={adminEditLabelStyle}>Details of accident/injury</label>
                        <textarea className="form-control" style={adminEditInputStyle} rows="3" value={editReport.accidentDetailsOfInjury} onChange={e=>setEditReport({...editReport, accidentDetailsOfInjury: e.target.value})}></textarea>
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Date</label>
                        <div className="site-register-datepicker-wrap">
                          <DatePicker
                            {...siteRegisterDatePickerProps}
                            selected={parseSiteRegisterDate(editReport.dateOfficial)}
                            onChange={(d) => setEditReport({...editReport, dateOfficial: formatSiteRegisterDate(d)})}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label style={adminEditLabelStyle}>Official Signature</label>
                      <div style={adminEditSigBoxStyle()}>
                        {editReport?.signatureOfficial ? (
                          <img 
                            src={editReport.signatureOfficial.startsWith('data:image') 
                              ? editReport.signatureOfficial 
                              : `data:image/png;base64,${editReport.signatureOfficial}`}
                            alt="Official Signature" 
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100px',
                              objectFit: 'contain',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              padding: '4px',
                              backgroundColor: 'white',
                              opacity: 1,
                              filter: 'contrast(1.1) brightness(1)'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={{
                            color: '#888',
                            fontSize: '14px',
                            padding: '20px'
                          }}>
                            Signature (Disabled in Edit)
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Employers Use Only */}
                <div className="row g-3 mb-3">
                  <div className="col-12">
                    <h6 style={{ color: ADMIN_THEME, fontWeight: 'bold', marginBottom: '15px' }}>4. EMPLOYERS USE ONLY</h6>
                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <p style={{color: '#000', marginBottom: '15px'}}>If this incident is reportable under RIDDOR (Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 1995)</p>
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>How was it reported?</label>
                        <input type="text" className="form-control" style={adminEditInputStyle} value={editReport.howReported} onChange={e=>setEditReport({...editReport, howReported: e.target.value})} />
                      </div>
                      <div className="col-12 col-md-6">
                        <label style={adminEditLabelStyle}>Date</label>
                        <div className="site-register-datepicker-wrap">
                          <DatePicker
                            {...siteRegisterDatePickerProps}
                            selected={parseSiteRegisterDate(editReport.dateEmployers)}
                            onChange={(d) => setEditReport({...editReport, dateEmployers: formatSiteRegisterDate(d)})}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-2">
                      <label style={adminEditLabelStyle}>Employers Signature</label>
                      <div style={adminEditSigBoxStyle()}>
                        {editReport?.signatureEmployers ? (
                          <img 
                            src={editReport.signatureEmployers.startsWith('data:image') 
                              ? editReport.signatureEmployers 
                              : `data:image/png;base64,${editReport.signatureEmployers}`}
                            alt="Employers Signature" 
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100px',
                              objectFit: 'contain',
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              padding: '4px',
                              backgroundColor: 'white',
                              opacity: 1,
                              filter: 'contrast(1.1) brightness(1)'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div style={{
                            color: '#888',
                            fontSize: '14px',
                            padding: '20px'
                          }}>
                            Signature (Disabled in Edit)
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12">
                        <p style={{color: '#000', fontSize: '12px', marginTop: '15px'}}>Please Note: To comply with the Data Protection Act 1998 (DPA) personal details entered on accident record forms must be kept confidential.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Accident Report File"
        importPath="accident-report/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setAccidentReports((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete Accident Report</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this accident report?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default AccidentReportPage;
